import React, { useEffect, useState } from "react";
import { Confirm, useNotify, useRefresh } from "react-admin";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Tooltip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { agentsApi, configSettingsApi } from "../../providers/env";
import EditIcon from "@mui/icons-material/Edit";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useCustomPermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/PermissionGuard';

/* =========================
 * LiveChat Toggle Button (works in custom Grid)
 * ========================= */
const AgentLiveChatToggle: React.FC<{
  agentId: string;
}> = ({ agentId }) => {
  const notify = useNotify();
  const [liveChatOn, setLiveChatOn] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch initial status
    const fetchStatus = async () => {
      try {
        const res = await configSettingsApi.getLivechatConfig({ agentId });
        if (res.data) {
          setLiveChatOn(res.data.LiveChatOn ?? false);
        }
      } catch (err) {
        // console.error("Error fetching livechat status", err);
      } finally {
        setLoading(false);
      }
    };
    if (agentId) fetchStatus();
  }, [agentId]);

  const handleToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Prevent card click
    const newValue = e.target.checked;
    setLiveChatOn(newValue); // Optimistic update

    try {
      await configSettingsApi.updateConfig({
        configSettingsUpdate: {
          agent_id: agentId,
          Channel: "livechat",
          LiveChatOn: newValue,
        },
      });
      notify("LiveChat status updated", { type: "success" });
    } catch (error: any) {
      setLiveChatOn(!newValue); // Revert on error
      notify(error?.message || "Error updating LiveChat status", { type: "error" });
    }
  };

  if (loading) return null; // or small spinner

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 1, // Margin top to separate from other actions if stacked, or adjust layout
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Livechat On</Typography>
        <Tooltip title="Enabled agent to connect to LiveChat" arrow>
          <HelpOutlineIcon
            color="action"
            sx={{ fontSize: 16, cursor: "pointer" }}
          />
        </Tooltip>
      </Box>
      <Switch
        size="small"
        checked={liveChatOn}
        onChange={handleToggle}
      />
    </Box>
  );
};
/* =========================
 * Add / Edit Agent Modal
 * ========================= */
const AddAgentModal: React.FC<{
  open: boolean;
  onClose: () => void;
  resourceName: string;
  fetchAgents: () => Promise<void>;
  formData?: any;
}> = ({ open, onClose, resourceName, fetchAgents, formData }) => {
  const notify = useNotify();
  const [form, setForm] = useState({
    name: "",
    description: "",
    status: false,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, status: e.target.checked }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (formData?.id) {
        await agentsApi.updateAgent({
          agentId: formData.id,
          agentUpdate: { ...form },
        });
        notify("Updated successfully", { type: "success" });
      } else {
        await agentsApi.createAgent({
          agentCreate: { ...form },
        });
        notify("Created successfully", { type: "success" });
      }
      onClose();
      await fetchAgents();
    } catch (error: any) {
      const msg = formData?.id
        ? "Error updating record"
        : "Error creating record";
      notify(error?.message || msg, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({
        name: formData?.name || "",
        description: formData?.description || "",
        status: formData?.status ?? false,
      });
    }
  }, [open, formData]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {formData?.id
          ? `Edit ${resourceName.slice(0, -1)}`
          : `Add new ${resourceName.slice(0, -1)}`}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          margin="normal"
          label="Name"
          name="name"
          fullWidth
          required
          value={form.name}
          onChange={handleChange}
        />
        <TextField
          margin="normal"
          label="Description"
          name="description"
          fullWidth
          required
          multiline
          rows={4}
          value={form.description}
          onChange={handleChange}
        />
        {/* Removed LiveChat toggle from here as requested */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={saving} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* =========================
 * Clone Button (works in custom Grid)
 * ========================= */
const CloneAgentButton: React.FC<{
  record: any;
  isExpired?: boolean;
  fetchAgents: () => Promise<void>;
}> = ({ record, isExpired, fetchAgents }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  const disabled = !record?.id || !!isExpired || loading;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    try {
      setLoading(true);
      await agentsApi.cloneAgent({ agentId: record.id });
      notify("Cloned agent successfully", { type: "success" });
      fetchAgents();
    } catch (err: any) {
      console.error("Error cloning agent", err);
      notify(err?.message || "Clone failed", { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={disabled ? "Unavailable" : "Copy agent"}>
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          tabIndex={-1}
        >
          <CopyAllIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

/* =========================
 * Delete with Confirm
 * ========================= */
const DeleteWithConfirm: React.FC<{
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
}> = ({ onConfirm, isLoading }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleConfirm = async () => {
    await onConfirm();
    setOpen(false); // đóng confirm sau khi xong
  };

  return (
    <>
      <Tooltip title={"Delete agent"}>
        <IconButton size="small" onClick={handleOpen} tabIndex={-1}>
          <DeleteIcon sx={{ color: "red" }} fontSize="small" />
        </IconButton>
      </Tooltip>
      <Confirm
        isOpen={open}
        loading={!!isLoading}
        title="Are you sure?"
        content="You won't be able to revert this!"
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

/* =========================
 * Main Grid
 * ========================= */
const AgentGrid: React.FC<{ resourceName?: string; isExpired?: boolean }> = ({
  resourceName = "agents",
  isExpired = false,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [formData, setFormData] = useState<any | undefined>();
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const navigate = useNavigate();
  const notify = useNotify();
  const { hasPermission, hasAgentAccess } = useCustomPermissions();

  const fetchAgents = async () => {
    try {
      const res = await agentsApi.getAgents();
      setData(res.data || []);
    } catch (err) {
      console.error("Error fetching agents", err);
      notify("Failed to fetch agents", { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    localStorage.removeItem("agentId");
    localStorage.removeItem("agentName");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateStatus = async (record: any, checked: boolean) => {
    try {
      await agentsApi.updateAgent({
        agentId: record.id,
        agentUpdate: { ...record, status: checked },
      });
      setData((prev) =>
        prev.map((item) =>
          item.id === record.id ? { ...item, status: checked } : item
        )
      );
      notify("Status updated successfully", { type: "success" });
      // nếu cần luôn đồng bộ server mới fetch lại:
      await fetchAgents();
    } catch (error: any) {
      notify(error?.message || "Error updating status", { type: "error" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setIsLoadingDelete(true);
      await agentsApi.deleteAgent({ agentId: id });
      notify("Deleted successfully!", { type: "success" });
      await fetchAgents();
    } catch (err: any) {
      console.error("Delete failed:", err);
      notify(err?.response?.data?.detail || "Delete failed", { type: "error" });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  if (loading) {
    return (
      <div style={{ position: "fixed", top: "50%", right: "50%" }}>
        Loading...
      </div>
    );
  }

  return (
    <Box sx={{ p: 2 }} className="agent-component">
      <Grid container spacing={2}>
        {/* Add new card */}
        <PermissionGuard resource="agent" action="create">
          <Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
            <Card
              sx={{
                width: "100%",
                cursor: isExpired ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 100,
                height: "100%",
              }}
              onClick={() => {
                if (!isExpired) {
                  setFormData(undefined);
                  setOpenAdd(true);
                }
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h6">＋ Add new</Typography>
              </CardContent>
            </Card>
          </Grid>
        </PermissionGuard>

        {/* Records */}
        {data?.map((record: any) => (
          <Grid item key={record.id} xs={12} sm={6} md={6} lg={4} xl={4}>
            <Card
              variant="outlined"
              sx={{ width: "100%", cursor: "pointer", minHeight: 100 }}
              onClick={() => {
                if (!hasAgentAccess(record.id)) {
                  notify("You don't have access to this agent", { type: "warning" });
                  return;
                }
                try {
                  localStorage.setItem("agentId", record.id);
                  localStorage.setItem("agentName", record.name);
                } catch (e) {
                  console.warn("Cannot access localStorage", e);
                }
                navigate(`/dashboard/${record.id}`);
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Avatar sx={{ width: 48, height: 48 }}>
                  {record.name?.charAt(0).toUpperCase()}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {record.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    Description: {record.description}
                  </Typography>
                </Box>

                {/* Actions + status */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    {/* Clone */}
                    {hasPermission('agent', 'clone') && (
                      <CloneAgentButton
                        record={record}
                        isExpired={isExpired}
                        fetchAgents={fetchAgents}
                      />
                    )}

                    {/* Edit */}
                    {hasPermission('agent', 'edit') && (
                      <Tooltip title={"Edit agent"}>
                        <span>
                          <IconButton
                            size="small"
                            tabIndex={-1}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isExpired) {
                                setFormData(record);
                                setOpenAdd(true);
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {/* Delete with confirm */}
                    {hasPermission('agent', 'delete') && (
                      <DeleteWithConfirm
                        onConfirm={async () => {
                          await handleDelete(record.id);
                        }}
                        isLoading={isLoadingDelete}
                      />
                    )}
                  </Box>
                  
                  {/* LiveChat Toggle */}
                  <AgentLiveChatToggle agentId={record.id} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Modal Add/Edit */}
      <AddAgentModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        resourceName={resourceName}
        fetchAgents={fetchAgents}
        formData={formData}
      />
    </Box>
  );
};

const AgentList: React.FC = () => {
  return <AgentGrid />;
};

export default AgentList;
